import { ControlGroup } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomCheckbox from "components/Atoms/CustomCheckbox"
import CustomDateInput from "components/Atoms/CustomDateInput"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import CustomSwitch from "components/Atoms/CustomSwitch"
import StaticSelector from "components/Atoms/StaticSelector"
import StringInput from "components/Atoms/StringInput"
import ButtonMutation from "components/Molecules/ButtonMutation"
import DynamicMultiSelector from "components/Molecules/DynamicMultiSelector"
import FormController from "components/Molecules/FormController"
import { CURRENT_STOCK_UNITS } from "data/static.items"
import { GROUPS, SEARCH_BATCH_STOCKS } from "graphql/selector.queries.gql"
import { ADD_UPDATE_SELLABLE_ITEM_STOCK } from "graphql/sellable-item/sellable-item.mutations.gql"
import { useFormDisabled } from "hooks/useAuth"
import useErrors from "hooks/useErrors"
import { cloneDeep, pick, set } from "lodash-es"
import sellableStockModel from "models/sellable-stock.model"

import { PlusCircle } from "@phosphor-icons/react"
import CustomDialog from "components/Atoms/CustomDialog"
import SearchSelector from "components/Templates/SearchSelector"
import { useSearchBatchStockQuery } from "generated/graphql"
import { ADD_UPDATE_SELLABLE_ITEM_STOCK_VARIANT } from "graphql/sellable-item-variant/sellable-item-variant.mutations.gql"
import SellableItemRegistration from "pages/registration/sellableitem/SellableItemRegistration"
import React, { useEffect, useMemo, useState } from "react"
import { useCurrentDc } from "store/auth.redux"
import AppToaster from "utils/AppToaster"
import { camelCaseShallow, getId, parseJson } from "utils/custom.utils"
import { graphqlError } from "utils/parseError"
import AlternateStockDropdown from "./AlternateStockDropdown"
import DisplayStockNote from "./DisplayStockNote"
import ItemBenchmarkDialog from "./ItemBenchmarkDialog"
const groupLabel = g => g?.category?.name ?? g?.name

const rupee = v => (v || v === 0 ? `₹ ${v}` : undefined)

const orderLimit = (conditions?: any) => {
  const [min, max] = conditions?.order_limit ?? []
  return min || max ? `${min ?? "-"} - ${max ?? "-"}` : undefined
}

const orderingConditions = (conditions?: any) =>
  [
    conditions?.is_pick_up && "Pick up",
    conditions?.is_home_delivery && "Home delivery",
    conditions?.is_cod_available && "Cash",
    conditions?.is_online_payment_available && "Online",
  ].filter(Boolean) as string[]

const SUMMARY_BORDER = "1px solid #d1d5db"

const SummaryTable = ({ rows }: { rows: any[][] }) => {
  const items = rows.filter(Boolean)
  const pairs: any[][][] = []
  for (let i = 0; i < items.length; i += 2) pairs.push(items.slice(i, i + 2))
  const cell = (v: any) => (v || v === 0 ? v : "—")
  return (
    <table
      className="w-full text-sm"
      style={{ borderCollapse: "collapse", border: SUMMARY_BORDER }}
    >
      <tbody>
        {pairs.map((pair, i) => (
          <tr key={i}>
            {[0, 1].map(j => (
              <React.Fragment key={j}>
                <td
                  className="w-1/5 px-3 py-2 font-bold text-gray-700"
                  style={{ border: SUMMARY_BORDER }}
                >
                  {pair[j]?.[0]}
                </td>
                <td
                  className="w-[30%] px-3 py-2 font-semibold text-gray-900"
                  style={{ border: SUMMARY_BORDER }}
                >
                  {pair[j] ? cell(pair[j][1]) : ""}
                </td>
              </React.Fragment>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const StockSummary = ({
  title,
  subtitle,
  subtitleLabel,
  isLive,
  groups,
  unitRate,
  discountedRate,
  stockRows,
  conditions,
  otherRows,
}: {
  title?: string
  subtitle?: string
  subtitleLabel?: string
  isLive?: boolean
  groups?: string[]
  unitRate?: any
  discountedRate?: any
  stockRows: any[][]
  conditions?: string[]
  otherRows?: any[][]
}) => (
  <SummaryTable
    rows={[
      ["Sellable item", title],
      [subtitleLabel ?? "Distribution centre", subtitle],
      ...(isLive !== undefined ? [["Stock live", isLive ? "Yes" : "No"]] : []),
      ["Group visibility", groups?.join(", ")],
      ["Unit rate", rupee(unitRate)],
      ["Discounted rate", rupee(discountedRate)],
      ...stockRows,
      ["Ordering conditions", conditions?.join(", ")],
      ...(otherRows ?? []),
    ]}
  />
)

const SellableItemStock = ({
  stock,
  onDelete,
  index,
  sellableItem,
  onCompleted,
  refetch,
}) => {
  const [dc] = useCurrentDc()
  const distributionCentre = dc
  const sellableItemId = sellableItem?.id
  const [isOpen, setOpen] = useState(false)
  const [openCreateModal, setOpenCreateModal] = useState(false)
  const [openStockSummary, setOpenStockSummary] = useState(false)
  const [variantSummary, setVariantSummary] = useState<any>(null)

  const [state, setState] = useState<any>({})

  const { data } = useSearchBatchStockQuery({
    variables: {
      filter: {
        sellableItemId: state?.sellableItem?.id,
      },
      first: 1,
    },
  })
  useEffect(() => {
    // Update the state whenever the props change
    setState({
      isLive: true,
      ...stock,
      orderingConditions: parseJson(stock?.orderingConditions),
      currentStockType:
        CURRENT_STOCK_UNITS.find(
          unit => unit.id === stock.currentStockType?.toLowerCase?.()
        ) || CURRENT_STOCK_UNITS[0],
      variantStocks: stock?.variantStocks?.map(st => {
        return {
          ...st,
          batchStock: data?.allBatchStocks?.[0],
          orderingConditions: parseJson(st?.orderingConditions),
          currentStockType:
            CURRENT_STOCK_UNITS.find(
              unit => unit.id === st.currentStockType?.toLowerCase?.()
            ) || CURRENT_STOCK_UNITS[0],
        }
      }),
    })
  }, [stock, data])

  function handleChange(path, value) {
    const stateCopy = cloneDeep(state)
    set(stateCopy, path, value)
    setState(stateCopy)
  }

  const isReseller = state?.groupVisibility?.find(
    g => g?.category?.codename === "RSL"
  )

  const parsedVariables = useMemo(() => {
    return {
      ...pick(state, [
        "id",
        "currentStockCount",
        "isLive",
        "unitRate",
        "expiryDate",
        "discountedRate",
        "offerStockCount",
      ]),
      ...(state?.currentStockType?.id === "percentage" && {
        currentStockPercentage: state?.currentStockPercentage ?? "",
      }),
      orderingConditions: camelCaseShallow(state?.orderingConditions),
      currentStockType: state?.currentStockType?.id,
      groupVisibility: state?.groupVisibility?.map(getId),
      sellableItemId,
      relatedStockId: state?.relatedStock?.id,
      distributionCentreId: distributionCentre?.id,
      minRate: isReseller ? state?.minRate : undefined,
      maxRate: isReseller ? state.maxRate : undefined,
    }
  }, [state, isReseller, sellableItemId, distributionCentre])

  const [errors] = useErrors({
    state: parsedVariables,
    schema: sellableStockModel,
  })

  const disabled = useFormDisabled("stock", parsedVariables)

  // const [variantErrors] = useErrors({
  //   state: parsedVariablesVariant,
  //   schema: sellableStockVariantModel,
  // })

  return (
    <>
      <FormController
        disabled={disabled}
        disableAll={true}
        errors={errors?.details}
        source={state}
        onChange={handleChange}
      >
        <div className="vt-sellable-item-stock">
          <div className="flex justify-between p-3">
            <CustomSwitch
              className="vt-stock-live"
              label="Stock Live"
              large
              path="isLive"
            />
            <header className="dc">{distributionCentre?.name}</header>
            <div>
              <CustomButton
                icon="comparison"
                intent="success"
                text="Benchmark Rates"
                minimal
                onClick={e => setOpen(true)}
              />
              <CustomButton
                disabled={stock.id ? true : false}
                onClick={e => onDelete(state, index)}
                icon="trash"
                intent="danger"
                minimal
              />
            </div>
          </div>
          <hr className="my-0" />
          <section>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-6">
                <DynamicMultiSelector
                  label="Group Visibility"
                  fill
                  query={GROUPS}
                  variables={{ filter: { categories: ["RTL", "RSL"] } }}
                  path="groupVisibility"
                  popoverClassName="sk-group-visibility-popover"
                  renderLabel={item => {
                    return item?.category.name
                  }}
                />
              </div>

              <div className="col-span-3">
                <StringInput
                  leftIcon="rupee"
                  label="Unit Rate"
                  path="unitRate"
                  big={false}
                />
              </div>
              <div className="col-span-3">
                <StringInput
                  leftIcon="rupee"
                  label="Discounted Rate"
                  path="discountedRate"
                  helperText="Unitrate - Discount"
                  big={false}
                />
              </div>
            </div>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-3">
                <StringInput
                  big={false}
                  type="number"
                  path="currentStockCount"
                  label="Ground Stock"
                />
              </div>
              <div className="col-span-3">
                <StringInput
                  disabled={true}
                  big={false}
                  type="number"
                  path="holdStockCount"
                  label="Hold Stock"
                />
              </div>
              <div className="col-span-3">
                <ControlGroup fill>
                  <StringInput
                    disabled={state?.relatedStock?.id ? true : false}
                    big={false}
                    type="number"
                    min={0}
                    step={5}
                    max={
                      state?.currentStockType?.id === "percentage"
                        ? 100
                        : undefined
                    }
                    label={
                      state?.currentStockType?.id === "percentage"
                        ? "% of Ground Stock"
                        : "Display Stock"
                    }
                    path={
                      state?.currentStockType?.id === "percentage"
                        ? "currentStockPercentage"
                        : "offerStockCount"
                    }
                  />
                  <StaticSelector
                    disabled={state?.relatedStock?.id ? true : false}
                    big={false}
                    label="Unit Type"
                    items={CURRENT_STOCK_UNITS}
                    path="currentStockType"
                  />
                </ControlGroup>
              </div>

              <div className="col-span-3">
                <CustomFormGroup
                  fill
                  label="Order Limit"
                  helperText="Min-Max (100 - 150)"
                >
                  <ControlGroup fill>
                    <StringInput
                      className="mb-0"
                      type="number"
                      big={false}
                      placeholder="Min"
                      path="orderingConditions.order_limit.0"
                    />
                    <StringInput
                      className="mb-0"
                      type="number"
                      big={false}
                      placeholder="Max"
                      path="orderingConditions.order_limit.1"
                    />
                  </ControlGroup>
                </CustomFormGroup>
              </div>
            </div>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-6">
                <CustomFormGroup label="Ordering Conditions">
                  <div className="flex">
                    <CustomCheckbox
                      className="mr-3"
                      path="orderingConditions.is_pick_up"
                      label="Pick up"
                    />
                    <CustomCheckbox
                      className="mr-3"
                      path="orderingConditions.is_home_delivery"
                      label="Home delivery"
                    />
                    <CustomCheckbox
                      className="mr-3"
                      path="orderingConditions.is_cod_available"
                      label="Cash"
                    />{" "}
                    <CustomCheckbox
                      className="mr-3"
                      path="orderingConditions.is_online_payment_available"
                      label="Online"
                    />
                  </div>
                </CustomFormGroup>
              </div>
              {/* The below conditions are put to maintain layouts effectively under multiple combinations of conditional rendering */}
              {isReseller ? (
                <>
                  <div className="col-span-3">
                    <StringInput
                      leftIcon="rupee"
                      label="Min Rate"
                      path="minRate"
                      helperText="Minimum rate for resale"
                      big={false}
                    />
                  </div>
                  <div className="col-span-3">
                    <StringInput
                      leftIcon="rupee"
                      label="Max Rate"
                      path="maxRate"
                      helperText="Maximum rate for resale"
                      big={false}
                    />
                  </div>
                </>
              ) : (
                <>
                  <AlternateStockDropdown sellableItem={sellableItem} />
                  <div className="col-span-3">
                    <CustomDateInput
                      big={false}
                      path="expiryDate"
                      label="Expiry Date"
                    />
                  </div>
                </>
              )}
            </div>
            {isReseller ? (
              <div className="grid grid-cols-12 gap-4 mt-3">
                <AlternateStockDropdown sellableItem={sellableItem} />
                <div className="col-span-3">
                  <CustomDateInput
                    big={false}
                    path="expiryDate"
                    label="Expiry Date"
                  />
                </div>
              </div>
            ) : null}

            <section className="mt-5 bg-blue-50 p-3" hidden={!state?.id}>
              <div className="flex justify-between !items-center">
                <div>Variant</div>
                <CustomButton
                  icon={<PlusCircle size={20} />}
                  text={"Variant"}
                  intent="primary"
                  className="shadow-none"
                  onClick={() => {
                    handleChange("variantStocks", [...state?.variantStocks, {}])
                  }}
                />
              </div>
              <div className="mt-2">
                {state?.variantStocks?.map((variant, index) => {
                  const requiredStock = variant?.currentStockCount

                  const SIcapacity =
                    state?.sellableItem?.secondarySkuType?.capacity
                  const batchStock = variant?.batchStock?.currentStockCount

                  const totalStock = SIcapacity * batchStock
                  const remainingStock =
                    totalStock - requiredStock >= 0
                      ? totalStock - requiredStock
                      : 0
                  const remainingStockUnit = remainingStock / SIcapacity

                  const parsedVariablesVariant = {
                    batchStockInput: {
                      suppliedById: variant?.batchStock?.suppliedBy?.id,
                      manufacturedOn: variant?.batchStock?.manufacturedOn,
                      expiryOn: variant?.batchStock?.expiryOn,
                      id: variant?.id ? variant?.batchStock?.id : undefined,
                      codename: variant?.id
                        ? variant?.batchStock?.name
                        : undefined,
                      totalQuantity: variant?.currentStockCount,
                      currentStockCount: variant?.currentStockCount,
                    },
                    parentBatchStockId: variant?.batchStock?.id,
                    parentSellableItemStockId: state?.id,
                    sellableItemId: variant?.sellableItem?.id,
                    sellableItemStockInput: {
                      id: variant?.id,
                      currentStockCount: variant?.currentStockCount,
                      unitRate: variant?.unitRate,
                      discountedRate: variant?.discountedRate,
                      isLive: true,
                      distributionCentreId: dc?.id,
                      currentStockType: "unit",
                      orderingConditions: camelCaseShallow(
                        variant?.orderingConditions
                      ),
                      offerStockCount:
                        variant?.currentStockType?.id === "unit"
                          ? variant?.offerStockCount
                          : undefined,
                      currentStockPercentage:
                        variant?.currentStockType?.id === "percentage"
                          ? variant?.currentStockPercentage
                          : undefined,

                      groupVisibility: variant?.groupVisibility?.map(getId),
                    },
                  }

                  return (
                    <div>
                      <div className="grid grid-cols-4 gap-4 !border-1 !border-black">
                        <StaticSelector
                          label="Sellable items"
                          items={state?.sellableItem?.childSellableItems}
                          path={`variantStocks.${index}.sellableItem`}
                          onCreate={() => {
                            setOpenCreateModal(true)
                            return
                          }}
                        />

                        <SearchSelector
                          label="Batch no."
                          query={SEARCH_BATCH_STOCKS}
                          path={`variantStocks.${index}.batchStock`}
                          filter={{
                            sellableItemId: state?.sellableItem?.id,
                            suppliedById: distributionCentre?.id,
                          }}
                          helperText={
                            batchStock ? `${batchStock} Units` : undefined
                          }
                        />
                        <div className="col-span-2">
                          <DynamicMultiSelector
                            label="Group Visibility"
                            fill
                            query={GROUPS}
                            variables={{
                              filter: { categories: ["RTL", "RSL"] },
                            }}
                            path={`variantStocks.${index}.groupVisibility`}
                            popoverClassName="sk-group-visibility-popover"
                            renderLabel={item => {
                              return item?.category.name
                            }}
                          />
                        </div>
                        <StringInput
                          big={false}
                          label={"Unit Rate"}
                          leftIcon="rupee"
                          path={`variantStocks.${index}.unitRate`}
                        />
                        <StringInput
                          big={false}
                          type="number"
                          label={"Discount Rate"}
                          leftIcon="rupee"
                          path={`variantStocks.${index}.discountedRate`}
                        />
                        {/* <StringInput
                          big={false}
                          type="number"
                          label={"Display Stock"}
                          leftIcon="rupee"
                          path={
                            state?.currentStockType?.id === "percentage"
                              ? `variantStocks.${index}.currentStockPercentage`
                              : `variantStocks.${index}.offerStockCount`
                          }
                        /> */}
                        <ControlGroup fill>
                          <StringInput
                            // disabled={state?.relatedStock?.id ? true : false}
                            big={false}
                            type="number"
                            min={0}
                            step={5}
                            max={
                              state?.currentStockType?.id === "percentage"
                                ? 100
                                : undefined
                            }
                            label={
                              state?.currentStockType?.id === "percentage"
                                ? "% of Ground Stock"
                                : "Display Stock"
                            }
                            path={
                              variant?.currentStockType?.id === "percentage"
                                ? `variantStocks.${index}.currentStockPercentage`
                                : `variantStocks.${index}.offerStockCount`
                            }
                          />
                          <StaticSelector
                            // disabled={state?.relatedStock?.id ? true : false}
                            big={false}
                            label="Unit Type"
                            items={CURRENT_STOCK_UNITS}
                            path={`variantStocks.${index}.currentStockType`}
                          />
                        </ControlGroup>
                        <CustomFormGroup
                          fill
                          label="Order Limit"
                          helperText="Min-Max (100 - 150)"
                        >
                          <ControlGroup fill>
                            <StringInput
                              className="mb-0"
                              type="number"
                              big={false}
                              placeholder="Min"
                              path={`variantStocks.${index}.orderingConditions.order_limit.0`}
                            />
                            <StringInput
                              className="mb-0"
                              type="number"
                              big={false}
                              placeholder="Max"
                              path={`variantStocks.${index}.orderingConditions.order_limit.1`}
                            />
                          </ControlGroup>
                        </CustomFormGroup>

                        <StringInput
                          disabled
                          big={false}
                          type="number"
                          label={"Total stock"}
                          required
                          placeholder={totalStock ? totalStock : ""}
                        />
                        <StringInput
                          big={false}
                          type="number"
                          label={"Required stock"}
                          required
                          path={`variantStocks.${index}.currentStockCount`}
                          min={SIcapacity}
                          max={
                            SIcapacity * variant?.batchStock?.currentStockCount
                          }
                          step={SIcapacity}
                          error={
                            SIcapacity &&
                            (requiredStock > SIcapacity * batchStock ||
                              requiredStock % SIcapacity !== 0)
                              ? `Enter ${SIcapacity}, ${SIcapacity * 2}, ${
                                  SIcapacity * 3
                                } as per stock`
                              : ""
                          }
                          helperText={
                            SIcapacity
                              ? `Enter ${SIcapacity}, ${SIcapacity * 2}, ${
                                  SIcapacity * 3
                                } as per stock`
                              : ""
                          }
                        />

                        <StringInput
                          big={false}
                          type="number"
                          label={"Remain stock"}
                          path={`variantStocks.${index}.remainStock`}
                          disabled
                          placeholder={
                            remainingStock !== undefined ? remainingStock : ""
                          }
                          helperText={
                            remainingStockUnit
                              ? `${remainingStockUnit} Units`
                              : ""
                          }
                        />
                        <CustomFormGroup label="Ordering Conditions">
                          <div className="flex">
                            <CustomCheckbox
                              className="mr-3"
                              path={`variantStocks.${index}.orderingConditions.is_pick_up`}
                              label="Pick up"
                            />
                            <CustomCheckbox
                              className="mr-3"
                              path={`variantStocks.${index}.orderingConditions.is_home_delivery`}
                              label="Home delivery"
                            />
                            <CustomCheckbox
                              className="mr-3"
                              path={`variantStocks.${index}.orderingConditions.is_cod_available`}
                              label="Cash"
                            />
                            <CustomCheckbox
                              className="mr-3"
                              path={`variantStocks.${index}.orderingConditions.is_online_payment_available`}
                              label="Online"
                            />
                          </div>
                        </CustomFormGroup>
                      </div>
                      <div className={"w-full text-right"} hidden={!variant}>
                        <CustomButton
                          className=""
                          intent="none"
                          disabled={disabled}
                          icon={stock?.id ? "saved" : "add"}
                          onClick={() =>
                            setVariantSummary({
                              variables: parsedVariablesVariant,
                              variant,
                            })
                          }
                          text={"Save Variant"}
                        />
                      </div>
                      <CustomDialog
                        className="!max-w-5xl w-full"
                        light
                        icon="people"
                        title="Register Group"
                        isOpen={openCreateModal}
                        onClose={() => setOpenCreateModal(false)}
                      >
                        <SellableItemRegistration
                          onComplete={data => {
                            handleChange(
                              `variantStocks.${index}.sellableItem`,
                              data?.sellableItem
                            )
                            setOpenCreateModal(false)
                          }}
                          relatedItemId={state?.sellableItem?.id}
                        />
                      </CustomDialog>
                    </div>
                  )
                })}
              </div>
            </section>
            <hr className="my-0 mt-3" />
            <div className="flex justify-between mt-3">
              <DisplayStockNote stock={state} />
              <CustomButton
                dark
                disabled={disabled}
                icon={stock?.id ? "saved" : "add"}
                text={stock?.id ? "Save" : "Add"}
                onClick={() => {
                  if (errors?.latest) {
                    AppToaster.show({
                      message: errors?.latest,
                      intent: "danger",
                      icon: "cross",
                    })
                    return
                  }
                  setOpenStockSummary(true)
                }}
              />
            </div>
          </section>
        </div>
        <CustomDialog
          className="!max-w-4xl w-full"
          light
          icon="confirm"
          title={`Confirm stock ${stock?.id ? "update" : "creation"}`}
          isOpen={openStockSummary}
          onClose={() => setOpenStockSummary(false)}
          footerRightButton={
            <ButtonMutation
              disabled={disabled}
              icon={stock?.id ? "saved" : "add"}
              onCompleted={({ addUpdateSellableItemStock: asic }) => {
                const item = asic?.sellableItemStock
                AppToaster.show({
                  message: `Stock saved for item ${item?.sellableItem?.name} on  DC ${item?.distributionCentre.name}`,
                  intent: "success",
                  icon: "tick",
                })
                setOpenStockSummary(false)
                onCompleted(index, item)
              }}
              onError={error => {
                AppToaster.show({
                  message: graphqlError(error.message),
                  intent: "danger",
                  icon: "cross",
                })
              }}
              mutation={ADD_UPDATE_SELLABLE_ITEM_STOCK}
              variables={parsedVariables}
              text={stock?.id ? "Confirm & Save" : "Confirm & Add"}
            />
          }
        >
          <StockSummary
            title={state?.sellableItem?.name ?? sellableItem?.name}
            subtitle={distributionCentre?.name}
            isLive={state?.isLive}
            groups={state?.groupVisibility?.map(groupLabel)}
            unitRate={state?.unitRate}
            discountedRate={state?.discountedRate}
            stockRows={[
              ["Ground stock", state?.currentStockCount],
              [
                state?.currentStockType?.id === "percentage"
                  ? "% of ground stock"
                  : "Display stock",
                state?.currentStockType?.id === "percentage"
                  ? state?.currentStockPercentage
                  : state?.offerStockCount,
              ],
              ["Unit type", state?.currentStockType?.name],
              ["Order limit", orderLimit(state?.orderingConditions)],
            ]}
            conditions={orderingConditions(state?.orderingConditions)}
            otherRows={
              isReseller
                ? [
                    ["Min rate", rupee(state?.minRate)],
                    ["Max rate", rupee(state?.maxRate)],
                  ]
                : [
                    ["Expiry date", state?.expiryDate],
                    ["Alternate stock", state?.relatedStock?.name],
                  ]
            }
          />
        </CustomDialog>
        <CustomDialog
          className="!max-w-4xl w-full"
          light
          icon="confirm"
          title="Confirm variant save"
          isOpen={!!variantSummary}
          onClose={() => setVariantSummary(null)}
          footerRightButton={
            <ButtonMutation
              disabled={disabled}
              icon={stock?.id ? "saved" : "add"}
              onCompleted={() => {
                AppToaster.show({
                  message: `Variant saved successfully!`,
                  intent: "success",
                  icon: "tick",
                })
                setVariantSummary(null)
                refetch()
              }}
              onError={error => {
                AppToaster.show({
                  message: graphqlError(error.message),
                  intent: "danger",
                  icon: "cross",
                })
              }}
              mutation={ADD_UPDATE_SELLABLE_ITEM_STOCK_VARIANT}
              variables={variantSummary?.variables}
              text="Confirm & Save Variant"
            />
          }
        >
          {variantSummary ? (
            <StockSummary
              title={variantSummary.variant?.sellableItem?.name}
              subtitleLabel="Parent item"
              subtitle={state?.sellableItem?.name}
              groups={variantSummary.variant?.groupVisibility?.map(groupLabel)}
              unitRate={variantSummary.variant?.unitRate}
              discountedRate={variantSummary.variant?.discountedRate}
              stockRows={[
                ["Batch no.", variantSummary.variant?.batchStock?.name],
                ["Required stock", variantSummary.variant?.currentStockCount],
                [
                  variantSummary.variant?.currentStockType?.id === "percentage"
                    ? "% of ground stock"
                    : "Display stock",
                  variantSummary.variant?.currentStockType?.id === "percentage"
                    ? variantSummary.variant?.currentStockPercentage
                    : variantSummary.variant?.offerStockCount,
                ],
                ["Unit type", variantSummary.variant?.currentStockType?.name],
                [
                  "Order limit",
                  orderLimit(variantSummary.variant?.orderingConditions),
                ],
              ]}
              conditions={orderingConditions(
                variantSummary.variant?.orderingConditions
              )}
            />
          ) : null}
        </CustomDialog>
        <ItemBenchmarkDialog
          sellableItem={sellableItem}
          isOpen={isOpen}
          onClose={() => {
            setOpen(false)
          }}
        />
      </FormController>
    </>
  )
}

export default SellableItemStock
